import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Input, InputRef, Popover, Select } from 'antd';
import { StyledSearch, SearchDropdown, SearchRow } from './styled';
import { SearchOutlined } from '@ant-design/icons';
import { debounce } from 'lodash';

export type SearchOperator = 'contains' | 'not_contains';

const OPERATOR_OPTIONS = [
  { value: 'contains', label: 'Contains' },
  { value: 'not_contains', label: 'Not contains' },
];

function Search({ onSearch }) {
  const inputRef = useRef<InputRef>(null);
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [operator, setOperator] = useState<SearchOperator>('contains');

  const debouncedSearch = useMemo(
    () =>
      debounce((v: string, op: SearchOperator) => {
        onSearch(v.toLowerCase(), op);
      }, 200),
    [onSearch],
  );

  const handleChange = useCallback(
    (e) => {
      const v = e.target.value;
      setValue(v);
      debouncedSearch(v, operator);
    },
    [debouncedSearch, operator],
  );

  const handleOperatorChange = useCallback(
    (op: SearchOperator) => {
      setOperator(op);
      if (value !== '') {
        onSearch(value.toLowerCase(), op);
      }
    },
    [value, onSearch],
  );

  const handleClear = useCallback(() => {
    setValue('');
    onSearch('', operator);
    setOpen(false);
  }, [onSearch, operator]);

  const handleOpenChange = (visible: boolean) => {
    setOpen(visible);
    if (visible) {
      setTimeout(() => {
        inputRef.current?.focus({ cursor: 'start' });
      });
    }
    if (!visible && value === '') {
      setOpen(false);
    }
  };

  const stopPropagation = useCallback((e) => e.stopPropagation(), []);

  const searchContent = (
    <SearchDropdown onClick={stopPropagation}>
      <SearchRow>
        <Select
          value={operator}
          onChange={handleOperatorChange}
          options={OPERATOR_OPTIONS}
          size="small"
          popupMatchSelectWidth={false}
        />
        <Input
          ref={inputRef}
          placeholder="Search column..."
          allowClear
          value={value}
          onChange={handleChange}
          onClear={handleClear}
          size="small"
        />
      </SearchRow>
    </SearchDropdown>
  );

  return (
    <StyledSearch onClick={stopPropagation} $active={value !== ''}>
      <Popover
        content={searchContent}
        trigger="click"
        open={open}
        onOpenChange={handleOpenChange}
        placement="bottomRight"
        arrow={false}
        overlayInnerStyle={{ padding: 0 }}
      >
        <SearchOutlined />
      </Popover>
    </StyledSearch>
  );
}

export default React.memo(Search);
